import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Loader, ArrowRight, Mail } from 'lucide-react';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { toast } from 'react-toastify';
import { activateClinicians, ActivateCliniciansPayload } from '../redux/clinic/clinicSlice';
import { useFinalizeClinicianPurchase } from '../hooks/Clinic/useFinalizeClinicianPurchase';
import { useRewardfulConversion } from '../hooks/Rewardful/useRewardfulConversion';
import { AppDispatch } from '../redux';

const PaymentSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);
  const [isActivating, setIsActivating] = useState(false);

  const activateCliniciansAfterPayment = useCallback(async (paymentIntentId: string) => {
    setIsActivating(true);
    try {
      // Get transactionId from localStorage (stored during payment creation)
      // The transactionId should be stored when payment intent is created
      const paymentData = localStorage.getItem('pendingPaymentData');
      let transactionId: string | undefined;
      let subscriptionId: string | undefined;
      let storedPaymentIntentId: string | undefined;
      
      if (paymentData) {
        try {
          const parsed = JSON.parse(paymentData);
          transactionId = parsed.transactionId;
          subscriptionId = parsed.subscriptionId;
          storedPaymentIntentId = parsed.paymentIntentId;
        } catch (e) {
          console.warn('Failed to parse payment data from localStorage:', e);
        }
      } else {
        console.warn('⚠️ No payment data found in localStorage');
      }

      // Use paymentIntentId from URL (most reliable) but prefer stored one if it exists
      // The URL paymentIntentId is the final one after Stripe processing
      const finalPaymentIntentId = paymentIntentId || storedPaymentIntentId || transactionId;

      // If transactionId is not in localStorage, use paymentIntentId as fallback
      const payload: ActivateCliniciansPayload = {
        transactionId: transactionId || finalPaymentIntentId, // Fallback to paymentIntentId
        subscriptionId: subscriptionId,
        paymentIntentId: finalPaymentIntentId,
      };

      
      const result = await dispatch(activateClinicians(payload)).unwrap();
      
      
      const activatedCount = result?.results?.activated ?? 0;
      toast.success(`Successfully activated ${activatedCount} clinician${activatedCount !== 1 ? 's' : ''}!`);
      
      // Clear payment data from localStorage
      localStorage.removeItem('pendingPaymentData');
    } catch (error: unknown) {
      console.error('❌ Error activating clinicians:', error);
      const errorMessage = typeof error === 'string' 
        ? error 
        : (error as { message?: string })?.message || 'Failed to activate clinicians';
      toast.error(`Payment succeeded but activation failed: ${errorMessage}`);
    } finally {
      setIsActivating(false);
    }
  }, [dispatch]);

  const { finalizePurchase } = useFinalizeClinicianPurchase();
  const { recordConversion } = useRewardfulConversion();

  /**
   * Reports an affiliate-attributed purchase.
   *
   * Must run before finalization: finalizePurchase deletes both
   * 'pendingScrapedClinicians' and 'pendingPurchaseBilling', which carry the
   * practice name and amount this record needs. No-ops for organic purchases.
   */
  const recordReferral = useCallback((intentId: string) => {
    const readJson = <T,>(key: string): T | null => {
      try {
        const raw = localStorage.getItem(key);
        return raw ? (JSON.parse(raw) as T) : null;
      } catch {
        return null;
      }
    };

    const pending = readJson<{ practiceId?: string; practiceName?: string }>('pendingScrapedClinicians');
    const billing = readJson<{ totalAmount?: number }>('pendingPurchaseBilling');

    recordConversion({
      transactionId: intentId,
      practiceId: pending?.practiceId,
      practiceName: pending?.practiceName,
      amount: billing?.totalAmount,
    });
  }, [recordConversion]);

  // Workforce flow: the selection and billing snapshot are persisted before payment
  // because Stripe's redirect lands here with no React state. The shared helper
  // creates the clinicians, records the transaction, and sends the invitations.
  const finalizeWorkforcePurchase = useCallback(async () => {
    try {
      const result = await finalizePurchase();
      if (result) {
        localStorage.removeItem('pendingPaymentData');
      }
    } catch (error: unknown) {
      console.error('❌ Error finalizing clinician purchase:', error);
      const message = (error as { message?: string })?.message;
      toast.error(message || 'Payment succeeded but adding clinicians failed. Please contact support.');
    }
  }, [finalizePurchase]);

  /**
   * Shared success path. Stripe confirms with redirect: 'always', so every
   * completed payment — 3D Secure or not — arrives here.
   */
  const handlePaymentSucceeded = useCallback((intentId: string) => {
    // Snapshot-dependent work first: finalization clears the snapshots.
    recordReferral(intentId);

    // Workforce purchase (clinicians selected from an import) finalizes here;
    // the legacy invited-clinicians flow activates by transaction instead.
    if (localStorage.getItem('pendingScrapedClinicians')) {
      finalizeWorkforcePurchase();
    } else {
      activateCliniciansAfterPayment(intentId);
    }
  }, [recordReferral, finalizeWorkforcePurchase, activateCliniciansAfterPayment]);

  useEffect(() => {
    const redirectStatus = searchParams.get('redirect_status');
    const intentId = searchParams.get('payment_intent');
    const paymentStatus = searchParams.get('payment'); // Optional, might not always be present


    // Clean up URL parameters
    window.history.replaceState({}, '', window.location.pathname);

    // Stripe redirects with redirect_status and payment_intent
    // Check redirect_status first (primary indicator from Stripe)
    if (redirectStatus === 'succeeded' && intentId) {
      setStatus('success');
      setPaymentIntentId(intentId);
      toast.success('Payment completed successfully!');
      handlePaymentSucceeded(intentId);
    } else if (redirectStatus === 'failed') {
      setStatus('error');
      toast.error('Payment failed. Please try again.');
    } else if (paymentStatus === 'success' && redirectStatus === 'succeeded') {
      // Fallback: if payment parameter is present and redirect_status is succeeded
      setStatus('success');
      setPaymentIntentId(intentId);
      toast.success('Payment completed successfully!');

      if (intentId) {
        handlePaymentSucceeded(intentId);
      }
    } else if (paymentStatus === 'success' && !redirectStatus) {
      // Payment status is success but redirect_status is not set (might be processing)
      setStatus('loading');
    } else if (intentId && !redirectStatus) {
      // We have a payment_intent but no redirect_status - might still be processing
      setStatus('loading');
    } else {
      // No payment parameters, might be a direct visit
      console.warn('⚠️ No valid payment parameters found');
      setStatus('error');
    }
  }, [searchParams, handlePaymentSucceeded]);

  const handleBackToInvitations = () => {
    navigate('/practice/workforce');
  };

  if (status === 'loading') {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto py-12 px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-lg p-8 text-center"
          >
            <div className="flex flex-col items-center justify-center py-12">
              <Loader className="w-16 h-16 text-blue-600 animate-spin mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Processing Payment</h2>
              <p className="text-gray-600">Please wait while we confirm your payment...</p>
            </div>
          </motion.div>
        </div>
      </DashboardLayout>
    );
  }

  if (status === 'error') {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto py-12 px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-lg p-8 text-center"
          >
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Failed</h2>
              <p className="text-gray-600 mb-6">
                We couldn't process your payment. Please try again or contact support if the problem persists.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={handleBackToInvitations}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <ArrowRight className="w-5 h-5" />
                  Back to Invitations
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto py-12 px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-lg p-8"
        >
          {/* Success Icon */}
          <div className="flex flex-col items-center justify-center py-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 15 }}
              className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6"
            >
              <CheckCircle className="w-12 h-12 text-green-600" />
            </motion.div>

            {/* Success Message */}
            <h1 className="text-3xl font-bold text-gray-900 mb-3">Payment Successful!</h1>
            <p className="text-gray-600 text-center mb-6 max-w-md">
              Your payment has been processed successfully. {isActivating ? 'Activating clinicians...' : 'Invitations will be sent to the selected clinicians.'}
            </p>
            {isActivating && (
              <div className="flex items-center justify-center gap-2 text-blue-600 mb-4">
                <Loader className="w-5 h-5 animate-spin" />
                <span className="text-sm">Activating clinicians...</span>
              </div>
            )}

            {/* Payment Details */}
            {paymentIntentId && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6 w-full max-w-md">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Payment Intent ID:</span>
                  <span className="text-sm font-mono text-gray-900">{paymentIntentId}</span>
                </div>
              </div>
            )}

            {/* What Happens Next */}
            <div className="bg-blue-50 rounded-lg p-6 mb-6 w-full max-w-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-600" />
                What happens next?
              </h3>
              <ul className="text-sm text-gray-700 space-y-2 text-left">
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-1">•</span>
                  <span>Invitations will be sent via email to the selected clinicians</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-1">•</span>
                  <span>Clinicians can complete their profiles and accept the invitation</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-1">•</span>
                  <span>You'll be notified once they complete their profiles</span>
                </li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 w-full max-w-md">
              <button
                onClick={handleBackToInvitations}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 font-medium"
              >
                <ArrowRight className="w-5 h-5" />
                Back to Invitations
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </DashboardLayout>
  );
};

export default PaymentSuccessPage;

